// src/js/index.tsx
import { isNotNull, requireNotNull } from "@enymo/ts-nullsafe";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// src/js/util.ts
function isAtomic(input) {
  return input instanceof File || input === null || typeof input !== "object";
}
function isSubsetRecursive(a, b) {
  if (isAtomic(a)) {
    return a === b;
  } else {
    for (const [key, value] of Array.isArray(a) ? a.entries() : Object.entries(a)) {
      const result = isSubsetRecursive(value, b[key]);
      if (!result) {
        return false;
      }
    }
    return true;
  }
}
function pruneUnchangedRecursive(input, comparison, target, ignoreKeys = []) {
  for (const [key, value] of Object.entries(input)) {
    if (isAtomic(value) || Array.isArray(value)) {
      if (ignoreKeys.includes(key) || !isSubsetRecursive(value, comparison[key])) {
        target[key] = value;
      }
    } else {
      target[key] = {};
      pruneUnchangedRecursive(value, comparison[key], target[key]);
    }
  }
}
function pruneUnchanged(input, comparison, ignoreKeys = []) {
  const target = {};
  pruneUnchangedRecursive(input, comparison, target, ignoreKeys);
  return target;
}

// src/js/index.tsx
function createResourceFactory({ adapter }) {
  return (resource, {
    defaultUpdateMethod = "on-success",
    pruneUnchanged: pruneUnchangedConfig = false,
    ...config
  } = {}) => {
    const ResourceContext = createContext(null);
    const { actionHook: useActions, eventHook: useEvent } = adapter(resource, config);
    const useResource = ({
      id,
      params,
      sorter,
      onCreated,
      onUpdated,
      onDestroyed,
      autoRefresh = true,
      ignoreContext = false,
      ...resourceConfig
    } = {}) => {
      const isArray = useCallback((input) => {
        return id === void 0;
      }, [id]);
      const actions = useActions(resourceConfig, params);
      const resourceContext = useContext(ResourceContext);
      const [localState, setState] = useState(id === void 0 ? [] : null);
      const state = useMemo(() => {
        if (!ignoreContext && isNotNull(resourceContext)) {
          if (id === void 0) {
            return resourceContext.state;
          } else {
            return resourceContext.state.find((value) => value.id === id) ?? null;
          }
        } else {
          return localState;
        }
      }, [ignoreContext, resourceContext?.state, localState]);
      const sortedState = useMemo(() => !isArray(state) || !sorter ? state : [...state].sort(sorter), [state, sorter, isArray]);
      const [meta, setMeta] = useState(null);
      const [error, setError] = useState(null);
      const [loading, setLoading] = useState(autoRefresh);
      const handleCreated = useCallback((item) => {
        if (onCreated?.(item) ?? true) {
          setState((prev) => prev.find((s) => s.id == item.id) ? prev.map((s) => s.id == item.id ? {
            ...s,
            ...item
          } : s) : [...prev, item]);
        }
      }, [onCreated, setState]);
      const handleUpdated = useCallback((item) => {
        if (onUpdated?.(item) ?? true) {
          setState((prev) => isArray(prev) ? prev.map((s) => s.id == item.id ? {
            ...s,
            ...item
          } : s) : { ...prev, ...item });
        }
      }, [onUpdated, setState]);
      const handleDestroyed = useCallback((delId) => {
        if (onDestroyed?.(delId) ?? true) {
          if (id !== void 0) {
            setState(null);
          } else {
            setState((prev) => prev.filter((s) => s.id !== delId));
          }
        }
      }, [onDestroyed, setState, id]);
      useEvent(params, "created", (ignoreContext || !isNotNull(resourceContext)) && id === void 0 ? async (item) => !loading && handleCreated(item) : void 0, [loading, handleCreated]);
      useEvent(params, "updated", ignoreContext || !isNotNull(resourceContext) ? async (item) => !loading && (id === void 0 || item.id === state.id) && handleUpdated(item) : void 0, [id, state, loading, handleUpdated]);
      useEvent(params, "destroyed", ignoreContext || !isNotNull(resourceContext) ? (delId) => !loading && (id === void 0 || delId === state.id) && handleDestroyed(delId) : void 0, [id, state, loading, handleDestroyed]);
      const store = useCallback(async (item = {}, updateMethodOverride, config2) => {
        const updateMethod = updateMethodOverride ?? defaultUpdateMethod;
        const promise = updateMethod !== "local-only" ? actions.store(item, config2) : null;
        if (updateMethod === "on-success" || id !== void 0) {
          const result = await promise;
          if (id === void 0) {
            handleCreated(result);
          }
          return result;
        } else {
          handleCreated(item);
          if (updateMethod !== "local-only") {
            const result = await promise;
            setState((prev) => prev.map((i) => i === item ? result : i));
            return result;
          }
          return item;
        }
      }, [actions.store, setState, handleCreated]);
      const batchStore = useCallback(async (items, updateMethodOverride, config2) => {
        const updateMethod = updateMethodOverride ?? defaultUpdateMethod;
        const promise = updateMethod !== "local-only" ? actions.batchStore(items, config2) : null;
        if (updateMethod === "on-success" || id !== void 0) {
          const result = await promise;
          if (id === void 0) {
            for (const item of result) {
              handleCreated(item);
            }
          }
          return result;
        } else {
          for (const item of items) {
            handleCreated(item);
          }
          if (updateMethod !== "local-only") {
            const result = await promise;
            setState((prev) => prev.map((i) => {
              const index = items.findIndex((item) => i === item);
              return index === -1 ? i : result[index];
            }));
            return result;
          }
          return items;
        }
      }, [actions.batchStore, setState, handleCreated]);
      const updateList = useCallback(async (id2, update, updateMethodOverride, config2) => {
        if (!ignoreContext && isNotNull(resourceContext)) {
          const comparison = pruneUnchangedConfig ? isArray(state) ? state.find((item) => item.id === id2) ?? null : state : null;
          return resourceContext.actions.update(id2, comparison ? pruneUnchanged(update, comparison) : update, updateMethodOverride, config2);
        }
        const updateMethod = updateMethodOverride ?? defaultUpdateMethod;
        const promise = updateMethod !== "local-only" ? actions.update(id2, update, config2) : null;
        if (updateMethod === "on-success") {
          handleUpdated(await promise);
        } else {
          handleUpdated({
            id: id2,
            ...update
          });
          if (promise) {
            handleUpdated(await promise);
          }
        }
      }, [state, resourceContext, ignoreContext, actions.update, state, handleUpdated]);
      const updateSingle = useCallback((update, updateMethodOverride, config2) => {
        return updateList(requireNotNull(id), update, updateMethodOverride, config2);
      }, [id, updateList]);
      const batchUpdate = useCallback(async (update, updateMethodOverride, config2) => {
        if (!ignoreContext && isNotNull(resourceContext)) {
          return resourceContext.actions.batchUpdate(update, updateMethodOverride, config2);
        }
        const updateMethod = updateMethodOverride ?? defaultUpdateMethod;
        const promise = updateMethod !== "local-only" ? actions.batchUpdate(update.map((update2) => {
          const comparison = pruneUnchangedConfig ? state.find((item) => item.id === update2.id) : void 0;
          return comparison ? pruneUnchanged(update2, comparison, ["id"]) : update2;
        }).filter((update2) => Object.keys(update2).length > 1), config2) : null;
        if (updateMethod === "on-success") {
          (await promise).map((update2) => handleUpdated(update2));
        } else {
          for (const item of update) {
            handleUpdated(item);
          }
          if (promise) {
            for (const item of await promise) {
              handleUpdated(item);
            }
          }
        }
      }, [state, resourceContext, ignoreContext, actions.batchUpdate, state, handleUpdated]);
      const destroyList = useCallback(async (id2, updateMethodOverride, config2) => {
        if (!ignoreContext && isNotNull(resourceContext)) {
          return resourceContext.actions.destroy(id2, updateMethodOverride, config2);
        }
        const updateMethod = updateMethodOverride ?? defaultUpdateMethod;
        const promise = updateMethod !== "local-only" && actions.destroy(id2, config2);
        if (updateMethod !== "immediate") {
          await promise;
        }
        handleDestroyed(id2);
        return promise;
      }, [resourceContext, ignoreContext, actions.destroy, handleDestroyed]);
      const destroySingle = useCallback((updateMethodOverride, config2) => destroyList(requireNotNull(id), updateMethodOverride, config2), [destroyList, id]);
      const batchDestroy = useCallback(async (ids, updateMethodOverride, config2) => {
        if (!ignoreContext && isNotNull(resourceContext)) {
          return resourceContext.actions.batchDestroy(ids, updateMethodOverride, config2);
        }
        const updateMethod = updateMethodOverride ?? defaultUpdateMethod;
        const promise = updateMethod !== "local-only" && actions.batchDestroy(ids, config2);
        if (updateMethod !== "immediate") {
          await promise;
        }
        for (const id2 of ids) {
          handleDestroyed(id2);
        }
        return promise;
      }, [ignoreContext, resourceContext, actions.batchDestroy, handleDestroyed]);
      const query = useCallback(async (action, data, params2, config2) => {
        const response = await actions.query(action, data, params2, config2);
        if (response.update === "replace" || id !== void 0) {
          setState(response.data);
        } else {
          for (const item of response.data) {
            handleCreated(item);
          }
          for (const id2 of response.destroy) {
            handleDestroyed(id2);
          }
        }
      }, [actions.query, setState, handleCreated, handleDestroyed]);
      const refresh = useCallback(async (config2, signal) => {
        if (ignoreContext || !isNotNull(resourceContext)) {
          try {
            setError(null);
            if (id !== null) {
              setLoading(true);
              try {
                const response = await actions.refresh(id, config2, signal);
                setMeta(response.meta);
                setState(response.data);
                setError(response.error);
              } catch (e) {
                if (!signal?.aborted) {
                  throw e;
                }
              }
            } else {
              setState(id === void 0 ? [] : null);
            }
          } finally {
            if (!signal?.aborted) {
              setLoading(false);
            }
          }
        }
      }, [setState, id, setLoading, setError, ignoreContext, resourceContext, actions.refresh]);
      useEffect(() => {
        if (autoRefresh) {
          const abortController = new AbortController();
          refresh(void 0, abortController.signal);
          return () => abortController.abort();
        }
      }, [refresh, autoRefresh, setError]);
      useEffect(() => {
        if (isNotNull(onCreated) && !ignoreContext && isNotNull(resourceContext)) {
          resourceContext.addCreatedListener(onCreated);
          return () => resourceContext.removeCreatedListener(onCreated);
        }
      }, [onCreated, ignoreContext, resourceContext]);
      useEffect(() => {
        if (isNotNull(onUpdated) && !ignoreContext && isNotNull(resourceContext)) {
          resourceContext.addUpdatedListener(onUpdated);
          return () => resourceContext.removeUpdatedListener(onUpdated);
        }
      }, [onUpdated, ignoreContext, resourceContext]);
      useEffect(() => {
        if (isNotNull(onDestroyed) && !ignoreContext && isNotNull(resourceContext)) {
          resourceContext.addDestroyedListener(onDestroyed);
          return () => resourceContext.removeDestroyedListener(onDestroyed);
        }
      }, [onDestroyed, ignoreContext, resourceContext]);
      return [
        sortedState,
        {
          ...!ignoreContext && isNotNull(resourceContext) ? {
            loading: resourceContext.actions.loading,
            refresh: resourceContext.actions.refresh,
            error: resourceContext.actions.error,
            meta: resourceContext.actions.meta,
            store: resourceContext.actions.store,
            batchStore: resourceContext.actions.batchStore,
            query: resourceContext.actions.query
          } : { loading, refresh, error, meta, store, batchStore, query },
          ...id !== void 0 ? { update: updateSingle, destroy: destroySingle } : { update: updateList, destroy: destroyList, batchUpdate, batchDestroy }
        }
      ];
    };
    const ResourceProvider = ({ params, children }) => {
      const createdListeners = useRef(/* @__PURE__ */ new Set());
      const updatedListeners = useRef(/* @__PURE__ */ new Set());
      const destroyedListeners = useRef(/* @__PURE__ */ new Set());
      const handleCreated = useCallback((item) => {
        for (const listener of createdListeners.current) {
          listener(item);
        }
      }, [createdListeners]);
      const handleUpdated = useCallback((item) => {
        for (const listener of updatedListeners.current) {
          listener(item);
        }
      }, [updatedListeners]);
      const handleDestroyed = useCallback((id) => {
        for (const listener of destroyedListeners.current) {
          listener(id);
        }
      }, [destroyedListeners]);
      const [state, actions] = useResource({
        params,
        onCreated: handleCreated,
        onUpdated: handleUpdated,
        onDestroyed: handleDestroyed
      });
      return /* @__PURE__ */ React.createElement(ResourceContext.Provider, { value: {
        state,
        actions,
        addCreatedListener: useCallback((listener) => createdListeners.current.add(listener), [createdListeners]),
        removeCreatedListener: useCallback((listener) => createdListeners.current.delete(listener), [createdListeners]),
        addUpdatedListener: useCallback((listener) => updatedListeners.current.add(listener), [updatedListeners]),
        removeUpdatedListener: useCallback((listener) => updatedListeners.current.delete(listener), [updatedListeners]),
        addDestroyedListener: useCallback((listener) => destroyedListeners.current.add(listener), [destroyedListeners]),
        removeDestroyedListener: useCallback((listener) => destroyedListeners.current.delete(listener), [destroyedListeners])
      } }, children);
    };
    return [useResource, ResourceProvider];
  };
}
export {
  createResourceFactory as default
};
